import { FeedbackAlert as Alert } from "../feedback/FeedbackAlert.js";
import {
  sceneObjectDefinitionGraph,
  sceneObjectEntryFromGraph,
  sceneObjectTypeError,
} from "../../../util/src/layout/scene-object-types";
import React, { useCallback } from "react";
import dico from "../../../util/src/language/dico";
import { Box } from "@mui/material";
import type {
  LayoutDocument,
  LayoutNode,
  SceneBehaviorCatalog,
  BehaviorGraph,
} from "../display-editor/layout-types";
import { SceneBehaviorBreadcrumb } from "./SceneBehaviorBreadcrumb";
import { SceneHubCanvas } from "./SceneHubCanvas";
import { SceneCatalogListCanvas } from "./SceneCatalogListCanvas";
import { BehaviorCanvas, type BehaviorGraphSnapshot } from "./BehaviorCanvas";
import type { BehaviorViewportState } from "./BehaviorCanvas";
import type { BehaviorCanvasClipboardHandle } from "./BehaviorCanvas";
import { sanitizeBehaviorGraph } from "../../../util/src/blueprint";
import type { BehaviorNode } from "../../../util/src/blueprint/types.js";

/**
 * Local-only navigation route inside a scene behavior tab.
 * Never persisted in the layout JSON.
 */
export type SceneBehaviorRoute =
  | { level: "hub" }
  | {
      level: "list";
      section: "animations" | "events" | "functions" | "variables" | "objects";
    }
  | {
      level: "detail";
      section: "animations" | "events" | "functions" | "variables" | "objects";
      entryId: string;
    };

export type SceneCatalogSection =
  | "animations"
  | "events"
  | "functions"
  | "variables"
  | "objects";

export interface SceneBehaviorNavigatorProps {
  route: SceneBehaviorRoute;
  onRouteChange: (route: SceneBehaviorRoute) => void;
  catalog: SceneBehaviorCatalog;
  /** Open a new scene-behavior tab at the given route. */
  onOpenNewTab?: (route: SceneBehaviorRoute) => void;
  /** Called when a catalog animation graph is updated. */
  setSceneCatalogAnimationGraph?: (
    entryId: string,
    graph: BehaviorGraph,
  ) => void;
  /** Called to update the full catalog (CRUD on entries). */
  setSceneCatalog?: (
    catalog:
      | SceneBehaviorCatalog
      | ((prev: SceneBehaviorCatalog) => SceneBehaviorCatalog),
  ) => void;
  document: LayoutDocument;
  sceneAnimationNames?: string[];
  sceneAnimationsByName?: Record<
    string,
    import("../../../util/src/blueprint/runtime/types.js").BehaviorAnimationPayload
  >;
  viewport?: BehaviorViewportState;
  onViewportChange?: (viewport: BehaviorViewportState) => void;
  canvasRef?: React.Ref<BehaviorCanvasClipboardHandle>;
  onPickFilterSvg?: () => Promise<string | null>;
  onPickFont?: () => Promise<string | null>;
  customFontFamilies?: string[];
  readOnly?: boolean;
}

const emptyGraph: BehaviorGraph = { nodes: [], connections: [] };

const SceneBehaviorNavigatorComponent: React.FC<
  SceneBehaviorNavigatorProps
> = ({
  route,
  onRouteChange,
  catalog,
  document,
  onOpenNewTab,
  setSceneCatalogAnimationGraph,
  setSceneCatalog,
  sceneAnimationNames = [],
  sceneAnimationsByName = {},
  viewport,
  onViewportChange,
  canvasRef,
  onPickFilterSvg,
  onPickFont,
  customFontFamilies,
  readOnly = false,
}) => {
  const handleNavigate = useCallback(
    (nextRoute: SceneBehaviorRoute) => {
      onRouteChange(nextRoute);
    },
    [onRouteChange],
  );

  const handleOpenNewTab = useCallback(
    (nextRoute: SceneBehaviorRoute) => {
      onOpenNewTab?.(nextRoute);
    },
    [onOpenNewTab],
  );

  const entryName =
    route.level === "detail"
      ? getEntryName(catalog, route.section, route.entryId)
      : undefined;

  const currentGraph =
    route.level === "detail"
      ? getEntryGraph(catalog, route.section, route.entryId)
      : undefined;

  const updateEntryGraph = useCallback(
    (entryId: string, section: SceneCatalogSection, graph: BehaviorGraph) => {
      if (section === "animations") {
        setSceneCatalogAnimationGraph?.(entryId, graph);
        return;
      }
      if (!setSceneCatalog) return;
      const updateGraphInList = <T extends { id: string }>(
        list: T[],
        graphKey: keyof T,
      ): T[] =>
        list.map((item) =>
          item.id === entryId ? { ...item, [graphKey]: graph } : item,
        );
      switch (section) {
        case "objects":
          setSceneCatalog((prev) => ({
            ...prev,
            objectTypes: prev.objectTypes?.map((entry) =>
              entry.id === entryId
                ? sceneObjectEntryFromGraph(entry, graph)
                : entry,
            ),
          }));
          break;
        case "events":
          setSceneCatalog((prev) => ({
            ...prev,
            customEvents: updateGraphInList(prev.customEvents, "triggerGraph"),
          }));
          break;
        case "functions":
          setSceneCatalog((prev) => ({
            ...prev,
            functions: updateGraphInList(prev.functions, "graph"),
          }));
          break;
        case "variables":
          setSceneCatalog((prev) => ({
            ...prev,
            variables: updateGraphInList(prev.variables, "graph"),
          }));
          break;
      }
    },
    [setSceneCatalog, setSceneCatalogAnimationGraph],
  );

  const handleNodesChange = useCallback(
    (nextNodes: BehaviorNode[]) => {
      if (route.level !== "detail") return;
      const graph = currentGraph ?? emptyGraph;
      const sanitized = sanitizeBehaviorGraph({
        nodes: nextNodes,
        connections: graph.connections,
      });
      updateEntryGraph(route.entryId, route.section, {
        nodes: nextNodes,
        connections: sanitized.connections,
      });
    },
    [route, currentGraph, updateEntryGraph],
  );

  const handleConnectionsChange = useCallback(
    (
      nextConnections: import("../../../util/src/blueprint/types.js").BehaviorConnection[],
    ) => {
      if (route.level !== "detail") return;
      const graph = currentGraph ?? emptyGraph;
      const sanitized = sanitizeBehaviorGraph({
        nodes: graph.nodes,
        connections: nextConnections,
      });
      updateEntryGraph(route.entryId, route.section, {
        nodes: graph.nodes,
        connections: sanitized.connections,
      });
    },
    [route, currentGraph, updateEntryGraph],
  );

  const handleGraphCommit = useCallback(
    ({ nodes, connections }: BehaviorGraphSnapshot) => {
      if (route.level !== "detail") return;
      const sanitized = sanitizeBehaviorGraph({ nodes, connections });
      updateEntryGraph(route.entryId, route.section, {
        nodes: sanitized.nodes,
        connections: sanitized.connections,
      });
    },
    [route, updateEntryGraph],
  );

  const handleFunctionPortsCommit = useCallback(
    (
      entryId: string,
      patch: {
        inputs: import("../display-editor/layout-types").SceneFunctionPort[];
        outputs: import("../display-editor/layout-types").SceneFunctionPort[];
        nodes: BehaviorNode[];
        connections: import("../../../util/src/blueprint/types.js").BehaviorConnection[];
      },
    ) => {
      if (!setSceneCatalog) return;
      const sanitized = sanitizeBehaviorGraph({
        nodes: patch.nodes,
        connections: patch.connections,
      });
      setSceneCatalog((prev) => ({
        ...prev,
        functions: prev.functions.map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                inputs: patch.inputs,
                outputs: patch.outputs,
                graph: {
                  nodes: sanitized.nodes,
                  connections: sanitized.connections,
                },
              }
            : entry,
        ),
      }));
    },
    [setSceneCatalog],
  );

  const handleVariablePortsCommit = useCallback(
    (
      entryId: string,
      patch: {
        inputs: import("../display-editor/layout-types").SceneVariableInputPort[];
        outputValueType: import("../../../util/src/blueprint/types.js").VariableValueType;
        outputObjectTypeId?: string;
        nodes: BehaviorNode[];
        connections: import("../../../util/src/blueprint/types.js").BehaviorConnection[];
      },
    ) => {
      if (!setSceneCatalog) return;
      const sanitized = sanitizeBehaviorGraph({
        nodes: patch.nodes,
        connections: patch.connections,
      });
      setSceneCatalog((prev) => ({
        ...prev,
        variables: prev.variables.map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                inputs: patch.inputs,
                outputValueType: patch.outputValueType,
                outputObjectTypeId: patch.outputObjectTypeId,
                graph: {
                  nodes: sanitized.nodes,
                  connections: sanitized.connections,
                },
              }
            : entry,
        ),
      }));
    },
    [setSceneCatalog],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <SceneBehaviorBreadcrumb
        route={route}
        entryName={entryName}
        onNavigate={handleNavigate}
      />
      {route.level === "detail" &&
        route.section === "objects" &&
        (() => {
          const entry = catalog.objectTypes?.find(
            (type) => type.id === route.entryId,
          );
          const error = entry && sceneObjectTypeError(entry, catalog);
          return error ? (
            <Alert severity="warning">
              {
                dico().behavior.sceneCatalog.objectEditor.errors[
                  error as keyof ReturnType<
                    typeof dico
                  >["behavior"]["sceneCatalog"]["objectEditor"]["errors"]
                ]
              }
            </Alert>
          ) : null;
        })()}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        {route.level === "hub" && (
          <SceneHubCanvas
            catalog={catalog}
            onNavigate={handleNavigate}
            onOpenNewTab={handleOpenNewTab}
          />
        )}
        {route.level === "list" && (
          <SceneCatalogListCanvas
            catalog={catalog}
            section={route.section}
            layoutNodes={document.scene.children}
            onNavigate={handleNavigate}
            onOpenNewTab={handleOpenNewTab}
            setSceneCatalog={setSceneCatalog}
            readOnly={readOnly}
          />
        )}
        {route.level === "detail" && (
          <BehaviorCanvas
            ref={canvasRef}
            node={
              {
                id: `scene-catalog-${route.entryId}`,
                kind: "container",
                layout: "absolute",
                x: 0,
                y: 0,
                width: 1,
                height: 1,
                zIndex: 0,
                children: [],
              } as LayoutNode
            }
            scope={
              route.section === "objects"
                ? "objectGraph"
                : route.section === "events"
                  ? "triggerGraph"
                  : route.section === "functions"
                    ? "functionGraph"
                    : route.section === "variables"
                      ? "variableGraph"
                      : "scene"
            }
            sceneAnimationNames={sceneAnimationNames}
            sceneAnimationsByName={sceneAnimationsByName}
            sceneCatalog={catalog}
            showSceneCatalogPanel
            catalogPanelSections={
              route.section === "animations" || route.section === "objects"
                ? ["variables", "objects"]
                : route.section === "functions"
                  ? ["animations", "functions", "variables", "objects"]
                  : route.section === "variables"
                    ? ["variables", "objects"]
                    : undefined
            }
            nodes={currentGraph?.nodes ?? []}
            connections={currentGraph?.connections ?? []}
            onNodesChange={handleNodesChange}
            onConnectionsChange={handleConnectionsChange}
            onBehaviorGraphCommit={handleGraphCommit}
            viewport={viewport}
            onViewportChange={onViewportChange}
            onPickFilterSvg={onPickFilterSvg}
            onPickFont={onPickFont}
            customFontFamilies={customFontFamilies}
            readOnly={readOnly}
            onOpenSceneCatalogEntry={handleNavigate}
            editingObjectTypeId={
              route.section === "objects" ? route.entryId : undefined
            }
            editingCustomEventId={
              route.section === "events" ? route.entryId : undefined
            }
            editingFunctionId={
              route.section === "functions" ? route.entryId : undefined
            }
            onFunctionPortsCommit={handleFunctionPortsCommit}
            editingVariableId={
              route.section === "variables" ? route.entryId : undefined
            }
            onVariablePortsCommit={handleVariablePortsCommit}
          />
        )}
      </Box>
    </Box>
  );
};

export const SceneBehaviorNavigator = React.memo(
  SceneBehaviorNavigatorComponent,
);

function getEntryName(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
  entryId: string,
): string | undefined {
  switch (section) {
    case "objects":
      return catalog.objectTypes?.find((o) => o.id === entryId)?.name;
    case "animations":
      return catalog.animations.find((a) => a.id === entryId)?.name;
    case "events":
      return catalog.customEvents.find((e) => e.id === entryId)?.name;
    case "functions":
      return catalog.functions.find((f) => f.id === entryId)?.name;
    case "variables":
      return catalog.variables.find((v) => v.id === entryId)?.name;
  }
}

function getEntryGraph(
  catalog: SceneBehaviorCatalog,
  section: SceneCatalogSection,
  entryId: string,
): BehaviorGraph | undefined {
  switch (section) {
    case "objects": {
      const entry = catalog.objectTypes?.find((type) => type.id === entryId);
      return entry ? sceneObjectDefinitionGraph(entry, catalog) : undefined;
    }
    case "animations":
      return catalog.animations.find((a) => a.id === entryId)?.graph;
    case "events":
      return catalog.customEvents.find((e) => e.id === entryId)?.triggerGraph;
    case "functions":
      return catalog.functions.find((f) => f.id === entryId)?.graph;
    case "variables":
      return catalog.variables.find((v) => v.id === entryId)?.graph;
  }
}
